import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { ListChecks, Languages, Calculator, Brain, X, Bot } from "lucide-react";
import { useLocation } from "wouter";
import { Sidebar } from "@/components/Sidebar";
import { Header } from "@/components/Header";
import { MCQPractice } from "@/pages/MCQPractice";
import EnglishGrid from "@/pages/english/EnglishGrid";
import { MathsPractice } from "@/pages/maths/MathsPractice";
import { ReasoningPractice } from "@/pages/reasoning/ReasoningPractice";
import { AIAssistantGrid } from "@/pages/ai/AIAssistantGrid";

interface MenuItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ size?: any }>;
}

export function PublicApp() {
  const [activeSection, setActiveSection] = useState("ai-assistant");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [location, setLocation] = useLocation();
  const [lang, setLang] = useState<"en" | "gu">("en");
  const [showNotice, setShowNotice] = useState(true);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleSectionChange = (section: string) => {
    setActiveSection(section);
    setSidebarOpen(false);
  };

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const menuItems: MenuItem[] = [
    { id: "ai-assistant", label: "Sanmaya AI", icon: Bot },
    { id: "mcq", label: "General Knowledge", icon: ListChecks },
    { id: "english", label: "English Grammar", icon: Languages },
    { id: "mathematics", label: "Mathematics", icon: Calculator },
    { id: "reasoning", label: "Reasoning", icon: Brain },
  ];

  const renderContent = () => {
    switch (activeSection) {
      case "ai-assistant":
        return <AIAssistantGrid />;
      case "mcq":
        return <MCQPractice />;
      case "mathematics":
        return <MathsPractice />;
      case "reasoning":
        return <ReasoningPractice />;
      case "english":
        return <EnglishGrid />;
      default:
        return <MCQPractice />;
    }
  };

  const noticeText = {
    en: "⚠️ Because of your inactivity, it may take 1–2 minutes to load… just like you before you drink tea 😉⏳",
    gu: "⚠️ તમારી નિષ્ક્રિયતા ના કારણે, લોડ થવામાં ૧–૨ મિનિટ લાગી શકે… બિલકુલ તમારી જેમ, જેવા કે તમે ચા પીધા પહેલાં 😉⏳",
  };

  const metaTitles: { [key: string]: string } = {
    mcq: "GCEP MCQ Practice | Smart Study",
    english: "English Grammar Practice | GCEP",
    mathematics: "Mathematics Practice | GCEP",
    reasoning: "Reasoning Practice | GCEP",
    "ai-assistant": "Sanmaya AI | GCEP",
  };

  return (
    <div className="flex min-h-screen gcep-gradient-bg">
      <Helmet>
        <title>{metaTitles[activeSection] || "GCEP GK Practice | Smart Study"}</title>
        <link rel="canonical" href="https://devibharat.com/" />
        <meta
          name="description"
          content="Practice MCQs, English grammar, mathematics, and reasoning for competitive exams with GCEP. Available in English and Gujarati."
        />
        <meta name="keywords" content="GCEP, MCQ practice, competitive exams, English, Mathematics, Reasoning, Gujarati, exam preparation" />
        <meta name="author" content="GCEP Group" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content={metaTitles[activeSection] || "GCEP MCQ Practice | Smart Study"} />
        <meta
          property="og:description"
          content="Practice MCQs, English grammar, mathematics, and reasoning for competitive exams with GCEP. Available in English and Gujarati."
        />
        <meta property="og:type" content="website" />
      </Helmet>

      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        activeSection={activeSection}
        onSectionChange={handleSectionChange}
        menuItems={menuItems}
        isPublic={true}
        onLoginClick={() => {
          setLocation("/login");
          setSidebarOpen(false);
        }}
      />

      <main className="flex-1 lg:ml-72 transition-all duration-300">
        <Header onSidebarToggle={handleSidebarToggle} />

        {/* {mounted && showNotice && (
          <div
            className="bg-yellow-100 dark:bg-yellow-900 text-yellow-900 dark:text-yellow-100 p-3 flex items-center justify-between text-sm"
            data-nosnippet
          >
            <span>{noticeText[lang]}</span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setLang(lang === "en" ? "gu" : "en")}
                className="px-2 py-1 text-xs rounded bg-yellow-200 dark:bg-yellow-800 hover:bg-yellow-300 dark:hover:bg-yellow-700"
              >
                {lang === "en" ? "ગુજરાતી" : "English"}
              </button>
              <button
                onClick={() => setShowNotice(false)}
                className="text-yellow-700 dark:text-yellow-200 hover:text-red-500"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        )} */}

        {renderContent()}
      </main>
    </div>
  );
}